import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function createStore() {
  const imgs = {};
  const subs = {};
  return {
    get: (i) => imgs[i],
    set: (i, src) => { imgs[i] = src; subs[i]?.(src); },
    sub: (i, fn) => { subs[i] = fn; return () => { delete subs[i]; }; },
  };
}

const Page = memo(forwardRef(({ store, i }, ref) => {
  const [src, setSrc] = useState(() => store.get(i));
  useEffect(() => store.sub(i, setSrc), [store, i]);
  return (
    <div ref={ref} className="bg-white" data-testid={`flipbook-page-${i + 1}`}>
      {src ? <img src={src} alt={`Page ${i + 1}`} className="w-full h-full object-contain select-none" draggable={false} />
        : <div className="w-full h-full flex items-center justify-center bg-[#f7f7f7]"><Loader2 className="animate-spin text-[#cccccc]" size={22} /></div>}
    </div>
  );
}));

function useBox(ref) {
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return box;
}

export default function PdfFlipbook({ url, fill = false }) {
  const wrapRef = useRef(null);
  const bookRef = useRef(null);
  const store = useMemo(createStore, [url]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const box = useBox(wrapRef);

  useEffect(() => {
    let cancelled = false;
    setMeta(null); setError(''); setPage(0);
    (async () => {
      try {
        const buf = await (await fetch(url)).arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const first = (await pdf.getPage(1)).getViewport({ scale: 1 });
        if (cancelled) return;
        setMeta({ n: pdf.numPages, ratio: first.height / first.width });
        const scale = Math.min(2.6, (1100 * Math.min(window.devicePixelRatio || 1, 2)) / 2 / first.width + 0.6);
        for (let i = 1; i <= pdf.numPages && !cancelled; i++) {
          const p = await pdf.getPage(i);
          const vp = p.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width; canvas.height = vp.height;
          await p.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
          store.set(i - 1, canvas.toDataURL('image/jpeg', 0.85));
        }
      } catch (e) {
        if (!cancelled) setError('Could not load this PDF.');
      }
    })();
    return () => { cancelled = true; };
  }, [url, store]);

  const availH = fill ? box.h - 72 : Math.round(window.innerHeight * 0.78);
  const spread = box.w >= 700;
  const pw = meta ? Math.floor(Math.max(140, Math.min(spread ? (box.w - 32) / 2 : box.w - 16, availH / meta.ratio))) : 0;
  const ph = meta ? Math.floor(pw * meta.ratio) : 0;

  const pages = useMemo(() => (meta ? Array.from({ length: meta.n }, (_, i) => <Page key={i} store={store} i={i} />) : null), [meta, store]);
  const onFlip = useCallback((e) => setPage(e.data), []);
  const flip = (dir) => { const pf = bookRef.current?.pageFlip(); if (pf) (dir < 0 ? pf.flipPrev() : pf.flipNext()); };

  useEffect(() => {
    if (!fill) return undefined;
    const onKey = (e) => { if (e.key === 'ArrowLeft') flip(-1); if (e.key === 'ArrowRight') flip(1); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fill]);

  const label = meta ? (spread && page > 0 && page < meta.n - 1 ? `Pages ${page + 1}–${page + 2}` : `Page ${page + 1}`) : '';

  return (
    <div ref={wrapRef} className={`w-full flex flex-col items-center justify-center ${fill ? 'h-full' : ''}`} data-testid="pdf-flipbook">
      {error ? (
        <div className="py-20 text-center text-[#999999]">
          <p>{error}</p>
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-[#D01919] font-medium hover:underline">Open the PDF instead</a>
        </div>
      ) : !meta || !box.w || pw <= 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-[#999999]">
          <Loader2 className="animate-spin text-[#D01919]" size={30} />
          <p className="mt-3 text-sm">Preparing your magazine…</p>
        </div>
      ) : (
        <>
          <div style={{ width: spread ? pw * 2 : pw, height: ph }}>
            <HTMLFlipBook
              key={`${pw}-${spread}`}
              ref={bookRef}
              width={pw}
              height={ph}
              size="fixed"
              startPage={page}
              usePortrait={!spread}
              showCover
              drawShadow
              maxShadowOpacity={0.4}
              flippingTime={700}
              useMouseEvents
              swipeDistance={20}
              showPageCorners
              mobileScrollSupport={false}
              onFlip={onFlip}
              className="shadow-2xl"
            >
              {pages}
            </HTMLFlipBook>
          </div>
          <div className="flex items-center gap-5 mt-5">
            <button onClick={() => flip(-1)} disabled={page === 0} data-testid="flipbook-prev" className="w-11 h-11 rounded-full bg-[#D01919] text-white flex items-center justify-center hover:bg-[#a51212] disabled:opacity-30 transition-colors"><ChevronLeft size={20} /></button>
            <span className="text-sm text-[#999999] text-center" data-testid="flipbook-page-label">{label} of {meta.n}<span className="hidden sm:inline"> · drag a page{fill ? ' or use ← →' : ''}</span></span>
            <button onClick={() => flip(1)} disabled={page >= meta.n - 1} data-testid="flipbook-next" className="w-11 h-11 rounded-full bg-[#D01919] text-white flex items-center justify-center hover:bg-[#a51212] disabled:opacity-30 transition-colors"><ChevronRight size={20} /></button>
          </div>
        </>
      )}
    </div>
  );
}
